import React, { useState, useEffect } from 'react';
import { Upload, Trash2, X, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ApiService from '../../services/api';

interface GalleryImage {
  id: string;
  imageUrl: string;
  title: string;
  description: string;
  uploadDate: string;
  sortOrder?: number;
}

interface GalleryManagementProps {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  isAuthenticated: boolean;
}

// Sortable Gallery Item Component
const SortableGalleryItem: React.FC<{
  image: GalleryImage;
  index: number;
  onDelete: (id: string) => void;
  getImageUrl: (url: string | undefined) => string;
  homeSettings: any;
}> = ({ image, index, onDelete, getImageUrl, homeSettings }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937',
        borderColor: isDragging ? '#ec4899' : (homeSettings?.secondaryBackgroundColor || '#374151')
      }}
      className={`rounded-lg shadow-sm overflow-hidden border-2 transition-all text-white ${
        isDragging ? 'shadow-lg scale-105' : ''
      }`}
    >
      <div className="relative">
        <img
          src={getImageUrl(image.imageUrl)}
          alt={image.title}
          className="w-full h-48 object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5SZXNpbSBZw7xrbGVuZW1lZGk8L3RleHQ+PC9zdmc+';
          }}
        />
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 bg-white/80 backdrop-blur-sm rounded-full p-1 cursor-grab hover:bg-white transition-colors"
        >
          <GripVertical className="w-4 h-4 text-gray-600" />
        </div>
        <div className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-full p-1">
          <button
            onClick={() => onDelete(image.id)}
            className="text-red-600 hover:text-red-800 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-semibold text-white mb-2">{image.title}</h4>
        <p className="text-sm text-gray-300 mb-3">{image.description}</p>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">{image.uploadDate}</span>
                     <span className="text-xs text-gray-400 px-2 py-1 rounded" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
            Sıra: {index + 1}
          </span>
        </div>
      </div>
    </div>
  );
};

const GalleryManagement: React.FC<GalleryManagementProps> = ({ setLoading, setError, isAuthenticated }) => {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageTitle, setImageTitle] = useState('');
  const [imageDescription, setImageDescription] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [homeSettings, setHomeSettings] = useState<any>(null);

  // Home settings'i yükle
  useEffect(() => {
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response);
      } catch (error) {
        console.error('Home settings yüklenirken hata:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Resim URL'ini oluşturan fonksiyon
  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return '/placeholder-image.jpg';
    }
    if (imageUrl.startsWith('/uploads/')) {
      // /uploads/image.jpg -> /api/proxy/uploads/image.jpg
      const filename = imageUrl.replace('/uploads/', '');
      return `http://localhost:5000/api/proxy/uploads/${filename}`;
    }
    return imageUrl;
  };

  // Galeri resimlerini yükle
  const loadGalleryImages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getGalleryImages();
      const images = response.images || [];
      // Sort by sortOrder if available, otherwise by upload date
      const sortedImages = images.sort((a: GalleryImage, b: GalleryImage) => {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
      });
      setGalleryImages(sortedImages);
    } catch (error: any) {
      console.error('Galeri resimleri yüklenirken hata:', error);
      setError('Galeri resimleri yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadGalleryImages();
    }
  }, [isAuthenticated]);

  const handleImageUpload = async () => {
    if (!selectedImage || !imageTitle) {
      setError('Lütfen resim ve başlık seçin');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('title', imageTitle);
      formData.append('description', imageDescription);

      await ApiService.uploadGalleryImage(formData);
      
      setSelectedImage(null);
      setImageTitle('');
      setImageDescription('');
      
      loadGalleryImages();
    } catch (error: any) {
      console.error('Resim yüklenirken hata:', error);
      setError('Resim yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id: string) => {
    if (!window.confirm('Bu resmi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await ApiService.deleteGalleryImage(id);
      setGalleryImages(prev => prev.filter(img => img.id !== id));
    } catch (error: any) {
      console.error('Resim silinirken hata:', error);
      setError('Resim silinemedi: ' + error.message);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setGalleryImages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update sort order in background
        setIsReordering(true);
        const reorderData = newItems.map((item, index) => ({
          id: parseInt(item.id),
          sortOrder: index
        }));

        console.log('Reorder data:', reorderData);
        console.log('Token exists:', !!localStorage.getItem('token'));
        console.log('Token value:', localStorage.getItem('token'));

        ApiService.reorderGalleryImages(reorderData)
          .then(() => {
            console.log('Reorder successful');
            setError(null);
          })
          .catch((error: any) => {
            console.error('Sıralama güncellenirken hata:', error);
            setError('Sıralama güncellenemedi: ' + error.message);
            // Reload original order on error
            loadGalleryImages();
          })
          .finally(() => {
            setIsReordering(false);
          });

        return newItems;
      });
    }
  };

  return (
    <div className="space-y-6 text-white overflow-y-auto">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Galeri Yönetimi</h2>
        {isReordering && (
          <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
            Sıralama güncelleniyor...
          </div>
        )}
      </div>

            {/* Upload Form */}
      <div className="rounded-lg shadow-sm p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Yeni Fotoğraf Yükle</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Fotoğraf Seç
            </label>
                         <input
               type="file"
               accept="image/*"
               onChange={(e) => setSelectedImage(e.target.files?.[0] || null)}
               className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
               style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}
             />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Başlık
            </label>
                         <input
               type="text"
               value={imageTitle}
               onChange={(e) => setImageTitle(e.target.value)}
               className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
               style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}
               placeholder="Fotoğraf başlığı"
             />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Açıklama
            </label>
                         <textarea
               value={imageDescription}
               onChange={(e) => setImageDescription(e.target.value)}
               rows={3}
               className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
               style={{ backgroundColor: homeSettings?.backgroundColor || '#000000', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}
               placeholder="Fotoğraf açıklaması (opsiyonel)"
             />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handleImageUpload}
              disabled={!selectedImage || !imageTitle}
              className="flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Upload className="w-4 h-4" />
              <span>Fotoğraf Yükle</span>
            </button>
          </div>
        </div>
      </div>

             {/* Gallery Grid with Drag & Drop */}
       <div className="rounded-lg shadow-sm p-6 border overflow-y-auto" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <h3 className="text-lg font-semibold text-white mb-4">
          Galeri Fotoğrafları
          <span className="text-sm font-normal text-gray-400 ml-2">
            (Sürükleyip bırakarak sıralayabilirsiniz)
          </span>
        </h3>
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={galleryImages.map(img => img.id)} strategy={rectSortingStrategy}>
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 max-h-none">
              {galleryImages.map((image, index) => (
                <SortableGalleryItem
                  key={image.id}
                  image={image}
                  index={index}
                  onDelete={deleteImage}
                  getImageUrl={getImageUrl}
                  homeSettings={homeSettings}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default GalleryManagement;